"use client";

/**
 * One-shot save celebration: a particle burst around the trigger icon
 * plus a floating benefit chip ("we're watching the price"). Render it
 * INSIDE a small `relative` wrapper around the ICON ITSELF - not the
 * whole button - so the burst radiates from and the chip floats above
 * the exact thing the user pressed (inside a wide button the effect
 * otherwise appears detached, over unrelated rows). Re-fires whenever
 * `burstKey` changes to a new non-zero value, renders nothing at 0.
 *
 * Pair with the `save-pop` class on the icon (key it by the same
 * burstKey so the animation restarts) and a `haptic()` call at the
 * trigger site. Keyframes live in globals.css; reduced-motion users
 * see none of it (the saved state itself is the feedback).
 */
import { useLayoutEffect, useRef, type CSSProperties } from "react";

const DOT_COUNT = 7;

export default function SaveCelebration({
  burstKey,
  color = "#111827",
  praise,
  placement = "above",
}: {
  /** 0 = idle; any new non-zero value (e.g. Date.now()) fires the burst. */
  burstKey: number;
  /** Particle colour (every 3rd dot is amber for a confetti feel). */
  color?: string;
  /** Optional benefit chip floated above the icon. */
  praise?: string;
  /** Where the praise chip goes. "below" for anchors at the top edge of
   *  a surface (a sheet's top bar), where "above" would be clipped. */
  placement?: "above" | "below";
}) {
  // The chip is centred on the icon; by a screen edge that put half of
  // it off screen (owner, 2026-10-10: "a notch appears outside the
  // screen"), and by a panel edge the panel's overflow cut it in half
  // (the listing drawer's heart, 2026-10-17). Measure once and shift it
  // back inside the nearest box that clips it, else the window.
  const chipRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = chipRef.current;
    if (!el) return;
    el.style.marginLeft = "";
    if (!el.offsetWidth) return; // reduced motion hides the chip
    // The first frame is scaled down (save-praise), so the box is
    // measured from its centre with the width from layout.
    const r = el.getBoundingClientRect();
    const cx = (r.left + r.right) / 2;
    const half = el.offsetWidth / 2;
    const pad = 8;
    let left = 0;
    let right = window.innerWidth;
    for (let n = el.parentElement; n; n = n.parentElement) {
      const ox = getComputedStyle(n).overflowX;
      if (ox !== "visible") {
        const b = n.getBoundingClientRect();
        left = Math.max(left, b.left);
        right = Math.min(right, b.right);
        break;
      }
    }
    const shift = Math.min(0, right - pad - (cx + half)) + Math.max(0, left + pad - (cx - half));
    if (shift) el.style.marginLeft = `${Math.round(shift)}px`;
  }, [burstKey, praise]);
  if (!burstKey) return null;
  return (
    <span key={burstKey} aria-hidden className="pointer-events-none absolute inset-0">
      {Array.from({ length: DOT_COUNT }, (_, i) => {
        const angle = (i / DOT_COUNT) * Math.PI * 2 - Math.PI / 2;
        const dist = 18 + (i % 2) * 7;
        return (
          <span
            key={i}
            className="save-particle absolute left-1/2 top-1/2 -ml-0.5 -mt-0.5 h-1 w-1 rounded-full"
            style={{
              backgroundColor: i % 3 === 2 ? "#fbbf24" : color,
              "--px": `${Math.round(Math.cos(angle) * dist)}px`,
              "--py": `${Math.round(Math.sin(angle) * dist)}px`,
            } as CSSProperties}
          />
        );
      })}
      {praise && (
        // Explicit hexes + ring: the dark theme remaps bg-gray-900 /
        // text-white utilities (the chip went dark-on-dark); literal
        // colours bypass the remap, the ring keeps an edge on dark
        // surfaces. z-20 so neighbouring rows can't sit on top of it.
        <span ref={chipRef} className={`save-praise absolute left-1/2 z-20 -translate-x-1/2 whitespace-nowrap ${placement === "below" ? "-bottom-8" : "-top-8"} rounded-full bg-[#111827]/95 px-2.5 py-1 text-[10px] font-bold text-[#f9fafb] shadow-lg ring-1 ring-white/20`}>
          {praise}
        </span>
      )}
    </span>
  );
}
